import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api } from '../api';

// Org scope for the operator portal (PRD §2.2/§2.9). A Tenant manages many Orgs; the switcher picks
// the active Org (or "All Orgs" = the tenant-wide / global view). `is_internal` marks the master
// tenant (Hook), which additionally gets the global framing. selectedOrgId=null → no org filter.
interface Org {
  id: string;
  name: string;
}
interface Tenant {
  name: string;
  is_internal: boolean;
  tier: string;
}
interface OrgState {
  orgs: Org[];
  tenant: Tenant | null;
  selectedOrgId: string | null;
  selectedOrg: Org | null;
  isMaster: boolean;
  setOrg: (id: string | null) => void;
  /** Create a new Org under this tenant; returns it (and adds it to the switcher). */
  createOrg: (name: string) => Promise<Org>;
  /** Append the active org filter to a /v1 path (no-op for the global view). */
  withOrg: (path: string) => string;
}

const Ctx = createContext<OrgState | null>(null);
const KEY = 'pavlov_org';

export function OrgProvider({ children }: { children: ReactNode }) {
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(() =>
    localStorage.getItem(KEY),
  );

  useEffect(() => {
    api
      .get<{ data: Org[] }>('/v1/orgs')
      .then((r) => setOrgs(r.data))
      .catch(() => {});
    api
      .get<Tenant>('/v1/tenant')
      .then(setTenant)
      .catch(() => {});
  }, []);

  function setOrg(id: string | null) {
    setSelectedOrgId(id);
    if (id) localStorage.setItem(KEY, id);
    else localStorage.removeItem(KEY);
  }

  async function createOrg(name: string): Promise<Org> {
    const org = await api.post<Org>('/v1/orgs', { name });
    setOrgs((cur) => [...cur, org]);
    return org;
  }

  function withOrg(path: string): string {
    if (!selectedOrgId) return path;
    return `${path}${path.includes('?') ? '&' : '?'}org_id=${encodeURIComponent(selectedOrgId)}`;
  }

  const selectedOrg = orgs.find((o) => o.id === selectedOrgId) ?? null;

  return (
    <Ctx.Provider
      value={{
        orgs,
        tenant,
        selectedOrgId,
        selectedOrg,
        isMaster: !!tenant?.is_internal,
        setOrg,
        createOrg,
        withOrg,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useOrg(): OrgState {
  const c = useContext(Ctx);
  if (!c) throw new Error('useOrg must be used within OrgProvider');
  return c;
}
